import { Packr, Unpackr } from 'msgpackr';
import { z } from 'zod';
export { diffView, patchView, SNAP_RING, SnapDecoder } from './delta.js';
/** Versions this build speaks. A client on any other version is closed with VERSION_MISMATCH. */
export const PROTOCOL_VERSION = 1;
export const SUPPORTED_VERSIONS = [1];
/** Largest client frame in bytes; the gateway sets uWS maxPayloadLength to this. */
export const MAX_CLIENT_FRAME = 2048;
export const CLOSE = {
    NORMAL: 1000,
    GOING_AWAY: 1001,
    TOO_BIG: 1009,
    /** The worker is draining or lost the room: reconnect through the api (redirect hint in bye). */
    RESTART: 1012,
    BAD_FRAME: 4400,
    AUTH_FAILED: 4401,
    FORBIDDEN: 4403,
    ROOM_GONE: 4404,
    AUTH_TIMEOUT: 4408,
    /** The same player opened a newer socket. */
    REPLACED: 4409,
    ROOM_FULL: 4410,
    VERSION_MISMATCH: 4426,
    RATE_LIMITED: 4429,
    /** Too many commands the room refused in a short while (BE-12): an edited client, not a phone. */
    POLICY: 1008,
};
/** Why a socket was closed with FORBIDDEN, as the phone shows it (a key in its strings table). */
export const KICK_REASON = { host: 'host_kick', office: 'removed by the park office' };
// ---------------------------------------------------------------- cosmetics
// The ids of everything coins buy that shows in a park (spec BE-26, FE-33). The design system draws
// them; these lists are what the wire accepts. Looks only: nothing here reaches the simulation.
export const LOADS = ['yams', 'mattress', 'jerrycan', 'speaker', 'plantain', 'cooler', 'luggage'];
export const SLOGANS = [
    'No food for lazy man',
    "God's time is best",
    'Sea never dry',
    'No condition is permanent',
    'Enter with your change',
    'Carry go',
    'Wahala dey',
];
export const FINISHES = ['gloss', 'pinstripe', 'ankara', 'flames', 'chrome', 'chequer'];
export const HORNS = ['classic', 'double', 'trailer', 'bell', 'okada', 'tune', 'agogo'];
/** Power skins: how a used boost trails behind your keke in a power-up park. Looks only. */
export const POWER_SKINS = ['exhaust', 'dust', 'spray', 'sparks', 'confetti'];
export const OWA_SCENES = ['bus', 'rain', 'owambe', 'harmattan', 'night'];
export const STICKERS = [
    'shine-eye',
    'na-you',
    'abeg',
    'lie-lie',
    'i-see-am',
    'calm-down',
    'wahala',
    'owa',
    'oya',
];
/**
 * Quick-chat phrases (BE-13, FE-23): sent as ids, never text, so they cost a few bytes, need no
 * keyboard and can't carry anything but what the phone's strings table says for them.
 */
export const QUICK_CHAT = [
    'not-me',
    'saw-it',
    'where',
    'stops',
    'lie',
    'skip',
    'vote-now',
    'ready',
    'good-game',
];
/** What a player wears in a park. Public: everyone in the park sees it. Never role data. */
export const lookSchema = z.strictObject({
    load: z.enum(LOADS).optional(),
    slogan: z.enum(SLOGANS).optional(),
    finish: z.enum(FINISHES).optional(),
    horn: z.enum(HORNS).optional(),
    owa: z.enum(OWA_SCENES).optional(),
    power: z.enum(POWER_SKINS).optional(),
});
/**
 * One of your buttons as the worker sees it (FE-18), carried in every view as `affordances`:
 * whether it belongs on the road (`may`), whether a press now would be taken (`now`), on what
 * (`id`), and when its cooldown ends (`at`, server ms). The worker builds it with buildView, so it
 * is the same shape in every view; the client reads it through this schema and treats anything
 * else as missing.
 */
export const affordanceSchema = z.strictObject({
    may: z.boolean(),
    now: z.boolean(),
    id: z.string().min(1).max(64).nullable(),
    at: z.number().int().nonnegative(),
});
/** Every button, always all of them (engine words). */
export const affordancesSchema = z.strictObject({
    task: affordanceSchema,
    report: affordanceSchema,
    button: affordanceSchema,
    fix: affordanceSchema,
    kill: affordanceSchema,
    sabotage: affordanceSchema,
    vent: affordanceSchema,
    ride: affordanceSchema,
    settle: affordanceSchema,
    send: affordanceSchema,
    /** Cut the Padlock at the Task Force Yard (arrest parks). */
    cut: affordanceSchema,
    /** Race: the pickup disc, and Go to leave the pump early. */
    pickup: affordanceSchema,
    go: affordanceSchema,
});
/** The schema and the simulation's type are one shape: if either drifts, this stops compiling. */
export const AFFORDANCES_MATCH = true;
/** Stickers per player per meeting, and the gap between two throws. */
export const STICKERS_PER_MEETING = 6;
export const STICKER_GAP_MS = 2_000;
// ---------------------------------------------------------------- client frames
const id = z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/);
const roomId = z.string().regex(/^[A-Za-z0-9_-]{1,32}$/);
const actionId = z.string().regex(/^[A-Za-z0-9_-]{1,32}$/);
const seq = z.number().int().min(0).max(0x7fffffff);
const axis = z.number().int().min(-127).max(127);
/** Why a player tells the park office about another (BE-17). */
export const FLAG_REASONS = ['cheating', 'abuse', 'name', 'other'];
const clientName = z.string().max(32);
/** Fields on every client frame. */
const base = { v: z.literal(PROTOCOL_VERSION), roomId, seq };
/** State-changing frames also carry an idempotency key. */
const action = { ...base, actionId };
/**
 * Frames about the round in play also name it (BE-12): the worker refuses one stamped for a round
 * that is over (a phone still pressing on the last round's results). Optional on the wire so a
 * phone that hasn't heard the round's id yet (the first snapshot beats the lobby frame) still drives.
 */
const matchId = z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/);
const inMatch = { ...base, matchId: matchId.optional() };
const matchAction = { ...action, matchId: matchId.optional() };
const frame = (t, shape) => z.strictObject({ t: z.literal(t), ...shape });
export const clientFrameSchema = z.discriminatedUnion('t', [
    frame('auth.hello', { ...base, ticket: z.string().min(16).max(1536), client: clientName }),
    frame('auth.resume', { ...base, resumeToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/), client: clientName }),
    frame('ping', { ...base, ct: z.number().finite() }),
    /** The stick; in a race also Brake (`b`, held while true). */
    frame('input', {
        ...inMatch,
        n: z.number().int().min(1).max(0x7fffffff),
        dx: axis,
        dy: axis,
        b: z.boolean().optional(),
    }),
    /** The last snapshot this client applied (BE-11), the baseline for the next delta; 0 asks for a full one. */
    frame('snap.ack', { ...base, sn: seq }),
    frame('kill', { ...matchAction, targetId: id }),
    frame('report', { ...matchAction, bodyId: id }),
    frame('button', { ...matchAction }),
    /** Use the power you hold (power-up parks). */
    frame('power', { ...matchAction }),
    /** Use your special role's ability (GP-17); a spotter, area boy or copy-copy names a keke. */
    frame('ability', { ...matchAction, targetId: id.optional() }),
    /** An agbero starts a wahala; anyone on the road fixes one at a repair station. */
    frame('wahala', { ...matchAction, kind: z.enum(['nepa', 'network', 'goslow', 'fuel']) }),
    frame('fix', { ...matchAction, stationId: id }),
    /** Passengers: stopped at a stop, drop the riders going here and load whoever is waiting. */
    frame('ride', { ...matchAction }),
    /** Area boys: answer the shake-down ("Oga, settle us."): Settle am pays the share, Pass refuses. */
    frame('settle', { ...matchAction, choice: z.enum(['settle', 'refuse']) }),
    /** VIO, once a round: send the agberos after a driver in sight. */
    frame('boys', { ...matchAction, targetId: id }),
    /** Bribe or Bail: the arrested driver's tap, at this many ms into the needle (the worker checks it). */
    frame('arrest', { ...matchAction, at: z.number().int().min(0).max(60_000) }),
    /** Cut the Padlock: one cut at the Task Force Yard's gate. */
    frame('cut', { ...matchAction }),
    /**
     * Race: tap the pickup disc (use), Go to leave the pump early with what is in the tank, the horn,
     * and the fuel-can run of a keke out of fuel (out with the can, fill it at a pump, pour it in).
     * The area boy's Settle am or Pass is the `settle` frame.
     */
    frame('race', { ...matchAction, op: z.enum(['use', 'go', 'horn', 'out', 'fill', 'pour']) }),
    /**
     * City Hustle, standing at the place it needs: take a waiting rider or drop one here (riderId),
     * buy fuel, the day ticket, a repair or a tyre, bank at a union office, settle the agbero latched
     * on, answer the police (pay or wait), and Bribe or Bail's tap (at: ms into the needle).
     */
    frame('hustle', {
        ...matchAction,
        op: z.enum(['take', 'drop', 'fuel', 'ticket', 'repair', 'tyre', 'bank', 'settle', 'pay', 'wait', 'tap']),
        riderId: z
            .string()
            .regex(/^r?[A-Za-z0-9]{1,16}$/)
            .optional(),
        at: z.number().int().min(0).max(60_000).optional(),
    }),
    /** Agberos: a shortcut under the bridge. Enter and move name the shortcut; exit needs none. */
    frame('shortcut', { ...matchAction, op: z.enum(['enter', 'move', 'exit']), ventId: id.optional() }),
    frame('task.interact', { ...matchAction, taskId: id }),
    /** A stop's game answer; the worker checks it against what it dealt. */
    frame('task.complete', {
        ...matchAction,
        taskId: id,
        outcome: z
            .strictObject({
            wires: z.array(z.number().int().min(0).max(3)).length(4).optional(),
            swipeMs: z.number().int().min(0).max(10_000).optional(),
            code: z
                .string()
                .regex(/^\d{4}$/)
                .optional(),
            fuelMs: z.number().int().min(0).max(20_000).optional(),
            pumps: z.number().int().min(0).max(50).optional(),
            clamps: z
                .array(z.enum(['left', 'right']))
                .length(2)
                .optional(),
            /** The oil sample or the passenger picked (inspect, spot). */
            pick: z.number().int().min(0).max(15).optional(),
            /** The note tiles in the order tapped (sort). */
            order: z.array(z.number().int().min(0).max(15)).max(16).optional(),
            /** The panels scrubbed (wash). */
            scrubbed: z.array(z.number().int().min(0).max(15)).max(16).optional(),
        })
            .optional(),
    }),
    frame('vote', { ...matchAction, target: z.union([z.literal('skip'), id]) }),
    frame('chat', { ...base, text: z.string().min(1).max(140) }),
    /** A quick-chat phrase by id (BE-13): the same channels and limits as typed chat. */
    frame('chat.quick', { ...base, phrase: z.enum(QUICK_CHAT) }),
    /**
     * Tell the park office about another player (BE-17). The worker attaches who they are and what
     * they said in chat lately; a person reviews it. Not a breakdown report (that is `report`).
     */
    frame('flag', {
        ...action,
        targetId: id,
        reason: z.enum(FLAG_REASONS),
        note: z.string().max(200).optional(),
    }),
    frame('sticker', { ...matchAction, sticker: z.enum(STICKERS), at: id }),
    frame('lobby.start', { ...action }),
    /** Arm or disarm a role ticket for the next round (free parks; private to this player). */
    frame('lobby.arm', { ...action, armed: z.boolean() }),
    /**
     * Ready (FE-07): this player is set for the next round. Shown on the roster; the host's start is
     * not gated on it. After a round it is Play again: when everyone here is ready the park reopens early.
     */
    frame('lobby.ready', { ...action, ready: z.boolean() }),
    /** Bots on (see the worker's bots switch): fill the park to its target now, or send them all home. */
    frame('lobby.bots', { ...action, fill: z.boolean() }),
    /** The host lists the park in Open parks (anyone can find it) or keeps it to people with the code. */
    frame('lobby.open', { ...action, open: z.boolean() }),
    /**
     * The host takes a player out of the lobby (BE-10). They can't come back to this park while it
     * lives: the worker and the seat reservation both refuse them.
     */
    frame('lobby.kick', { ...action, targetId: id }),
    frame('leave', { ...base }),
]);
/** Frames about the round in play: the phone stamps them with its id (`matchId`), the worker checks it. */
export const MATCH_SCOPED = new Set([
    'input',
    'kill',
    'report',
    'button',
    'power',
    'ability',
    'wahala',
    'fix',
    'ride',
    'settle',
    'boys',
    'arrest',
    'cut',
    'race',
    'hustle',
    'shortcut',
    'task.interact',
    'task.complete',
    'vote',
    'sticker',
]);
/** Rate classes enforced by the gateway, per connection, per second. */
export const RATE_LIMITS = { input: 30, chat: 2, action: 5, control: 4, ack: 15 };
export const RATE_CLASS = {
    'auth.hello': 'control',
    'auth.resume': 'control',
    ping: 'control',
    leave: 'control',
    input: 'input',
    'snap.ack': 'ack',
    chat: 'chat',
    'chat.quick': 'chat',
    kill: 'action',
    report: 'action',
    button: 'action',
    power: 'action',
    ability: 'action',
    wahala: 'action',
    fix: 'action',
    ride: 'action',
    settle: 'action',
    boys: 'action',
    arrest: 'action',
    cut: 'action',
    race: 'action',
    hustle: 'action',
    shortcut: 'action',
    'task.interact': 'action',
    'task.complete': 'action',
    vote: 'action',
    flag: 'action',
    sticker: 'action',
    'lobby.start': 'action',
    'lobby.arm': 'action',
    'lobby.ready': 'action',
    'lobby.bots': 'action',
    'lobby.open': 'action',
    'lobby.kick': 'action',
};
/** Over-limit frames in one minute before the socket is closed with RATE_LIMITED. */
export const RATE_VIOLATIONS_BEFORE_CLOSE = 10;
/**
 * Refused commands that a phone on a bad line doesn't make (a knock in cooldown, a driver's knock,
 * a game answer never dealt) in REFUSAL_WINDOW_MS before the room closes the socket with POLICY.
 * Refusals lag explains (out of range, too soon, wrong phase) don't count.
 */
export const REFUSALS_BEFORE_CLOSE = 20;
export const REFUSAL_WINDOW_MS = 60_000;
// ---------------------------------------------------------------- framing
// Plain maps only: records would make one connection's decoding depend on structures another
// connection defined.
const packr = new Packr({ useRecords: false, moreTypes: false });
const unpackr = new Unpackr({ useRecords: false, mapsAsObjects: true, moreTypes: false });
export function encode(frame) {
    return packr.pack(frame);
}
/** Decode one untrusted client frame. Order: size, msgpack, version, schema. */
export function decodeClientFrame(bytes) {
    if (bytes.byteLength > MAX_CLIENT_FRAME)
        return { ok: false, code: CLOSE.TOO_BIG, reason: 'frame too big' };
    let raw;
    try {
        raw = unpackr.unpack(bytes);
    }
    catch {
        return { ok: false, code: CLOSE.BAD_FRAME, reason: 'not msgpack' };
    }
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return { ok: false, code: CLOSE.BAD_FRAME, reason: 'not an object' };
    }
    const v = raw.v;
    if (typeof v !== 'number' || !SUPPORTED_VERSIONS.includes(v)) {
        return { ok: false, code: CLOSE.VERSION_MISMATCH, reason: 'unsupported protocol version', version: v };
    }
    const parsed = clientFrameSchema.safeParse(raw);
    if (!parsed.success) {
        const t = raw.t;
        return { ok: false, code: CLOSE.BAD_FRAME, reason: typeof t === 'string' ? `bad ${t.slice(0, 24)}` : 'bad frame' };
    }
    return { ok: true, frame: parsed.data };
}
/** Decode a server frame on the client. The server is trusted; this only checks the shape loosely. */
export function decodeServerFrame(bytes) {
    try {
        const raw = unpackr.unpack(bytes);
        return raw && typeof raw.t === 'string' ? raw : null;
    }
    catch {
        return null;
    }
}
/** Player-written text: no control, zero-width or bidi characters; whitespace collapsed; capped. */
export function cleanText(raw, max) {
    return (raw
        // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping control characters is the point
        .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max)
        .trim());
}
// ------------------------------------------------------------ phone telemetry (FE-28)
/**
 * What a phone reports about how the game runs on it, once a minute: counts in fixed buckets
 * (upper bounds, the last is "anything above"), so the api only adds numbers. No ids, no names, no
 * addresses: a device class, the release, and counts.
 */
export const TELEMETRY = {
    /** Frame time, ms: 120, 60, 40, 30 fps and slower. */
    frameMs: [9, 17, 25, 34, 50, 100],
    /** Ping round trip to the worker, ms. */
    rttMs: [50, 100, 200, 400, 800, 1600],
    /** Largest Contentful Paint and Interaction to Next Paint, ms. */
    lcpMs: [1000, 2500, 4000, 8000],
    inpMs: [100, 200, 500, 1000],
    /** Cumulative Layout Shift. */
    cls: [0.05, 0.1, 0.25, 0.5],
    /** Most frames one report may count (a minute at 120 fps, with slack). */
    maxCount: 20_000,
};
export const DEVICE_CLASSES = ['low', 'mid', 'high'];
/** Counts per bucket: one more than the bounds (the overflow). */
const counts = (bounds) => z.array(z.number().int().min(0).max(TELEMETRY.maxCount)).length(bounds.length + 1);
export const telemetryReportSchema = z
    .object({
    v: z.literal(1),
    device: z.enum(DEVICE_CLASSES),
    frames: counts(TELEMETRY.frameMs),
    rtt: counts(TELEMETRY.rttMs),
    reconnects: z
        .object({
        tried: z.number().int().min(0).max(1000),
        ok: z.number().int().min(0).max(1000),
        failed: z.number().int().min(0).max(1000),
    })
        .strict(),
    vitals: z
        .object({
        lcp: z.number().min(0).max(600_000).optional(),
        inp: z.number().min(0).max(600_000).optional(),
        cls: z.number().min(0).max(100).optional(),
    })
        .strict(),
})
    .strict();
/** Which bucket a value falls in: the first bound it does not exceed, else the overflow. */
export function bucketOf(bounds, value) {
    for (let i = 0; i < bounds.length; i++)
        if (value <= bounds[i])
            return i;
    return bounds.length;
}
//# sourceMappingURL=index.js.map